import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import { getAllSchemeSpecificLoginDetails } from "../utils/healthCareSliceFunc";
import {
  formatStageLabel,
  getFallbackPortalUrl,
  normalizePortalName,
} from "../utils/tpaPortalUrls";

export type TpaPortalPatientContext = {
  patientName: string;
  uhid: string;
  crNo: string;
  doa: string;
  claimId: string;
};

export type TpaPortalConfig = {
  portalUrl: string;
  portalName: string;
  schemeName: string;
  userName: string | null;
  stageLabel: string;
  usedFallbackUrl: boolean;
  patientContext: TpaPortalPatientContext;
};

type UseTpaPortalConfigArgs = {
  visitStageName?: string | null;
  enabled?: boolean;
};

export function useTpaPortalConfig({
  visitStageName,
  enabled = true,
}: UseTpaPortalConfigArgs): {
  config: TpaPortalConfig | null;
  loading: boolean;
} {
  const dispatch = useDispatch<AppDispatch>();
  const { patients, healthCares } = useSelector((state: RootState) => state);

  const casesResponse = patients?.fetchBasicDetails?.casesResponse;
  const patientResponse = patients?.fetchBasicDetails?.patientResponse;
  const visitInfoResponse = patients?.fetchBasicDetails?.visitInfoResponse;

  const healthCareId = casesResponse?.healthCareId;
  const schemeMetaDataId = casesResponse?.schemeMetaDataId;
  const portalName = normalizePortalName(
    casesResponse?.portalMetaDataNames?.[0]
  );
  const schemeName = String(
    casesResponse?.schemeMetaDataName ||
      casesResponse?.alternateName ||
      portalName ||
      "TPA"
  );

  useEffect(() => {
    if (!enabled || !healthCareId) return;
    dispatch(getAllSchemeSpecificLoginDetails({ healthCareId }));
  }, [dispatch, enabled, healthCareId]);

  const credentialMatch = useMemo(() => {
    const rows = Object.values(
      healthCares?.healthCareSchemeSpecificLoginDetails || {}
    ).filter((row) => !!row?.loginUrl?.trim());

    if (!rows.length) return null;

    // 1) Exact scheme match (preferred)
    if (schemeMetaDataId != null) {
      const byScheme = rows.find(
        (row) =>
          row?.schemeId != null &&
          Number(row.schemeId) === Number(schemeMetaDataId)
      );
      if (byScheme) return byScheme;
    }

    // 2) Hospital-level row with null schemeId (common when inserted manually in DB)
    const hospitalLevel = rows.find(
      (row) => row?.schemeId == null || row?.schemeId === undefined
    );
    if (hospitalLevel) return hospitalLevel;

    // 3) Single credential for this hospital — use it
    if (rows.length === 1) return rows[0];

    return null;
  }, [healthCares?.healthCareSchemeSpecificLoginDetails, schemeMetaDataId]);

  const loading =
    enabled &&
    !!healthCareId &&
    healthCares?.loading === true &&
    !credentialMatch;

  const config = useMemo((): TpaPortalConfig | null => {
    if (!enabled) return null;

    const loginUrl = credentialMatch?.loginUrl?.trim() || null;
    const fallbackUrl = getFallbackPortalUrl(portalName);
    const portalUrl = loginUrl || fallbackUrl;

    if (!portalUrl) return null;

    return {
      portalUrl,
      portalName: portalName || "PORTAL",
      schemeName,
      userName: credentialMatch?.userName
        ? String(credentialMatch.userName)
        : null,
      stageLabel: formatStageLabel(visitStageName),
      usedFallbackUrl: !loginUrl && !!fallbackUrl,
      patientContext: {
        patientName: String(
          patientResponse?.fullName || casesResponse?.patientName || "—"
        ),
        uhid: String(patientResponse?.uhidNo || "—"),
        crNo: String(
          visitInfoResponse?.creditNumber ||
            visitInfoResponse?.opNumber ||
            "—"
        ),
        doa: String(
          visitInfoResponse?.doa || casesResponse?.doa || "—"
        ),
        claimId: String(visitInfoResponse?.claimId || "—"),
      },
    };
  }, [
    enabled,
    credentialMatch,
    portalName,
    schemeName,
    visitStageName,
    patientResponse,
    visitInfoResponse,
    casesResponse,
  ]);

  return { config, loading };
}
